import { createSelector } from 'redux-orm';

import orm from '../orm';
import { selectPath } from './router';
import { selectCurrentUserId, selectHasPermission } from './users';
import { isLocalId } from '../utils/local-id';
import { RelationTypes, ResourceTypes } from '../constants/Enums'; // ─── NOUVEAU ───

export const makeSelectBoardById = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Board }, id) => {
      const boardModel = Board.withId(id);

      if (!boardModel) {
        return boardModel;
      }

      return boardModel.ref;
    },
  );

export const selectBoardById = makeSelectBoardById();

export const selectCurrentBoard = createSelector(
  orm,
  (state) => selectPath(state).boardId,
  ({ Board }, id) => {
    if (!id) {
      return id;
    }

    const boardModel = Board.withId(id);

    if (!boardModel) {
      return boardModel;
    }

    return boardModel.ref;
  },
);

export const selectCurrentBoardPermissions = (state) => {
  const board = selectCurrentBoard(state);

  console.log('BOARD');
  console.log(board);

  return board?.permissions || [];
};

export const selectCanViewBoard = (state) => {
  const { boardId } = selectPath(state);

  if (!boardId) {
    return false;
  }

  return selectHasPermission(state, 'Board', Number(boardId), 'board_view');
};

export const selectCanEditBoard = (state) => {
  const { boardId } = selectPath(state);

  if (!boardId) {
    return false;
  }

  return selectHasPermission(state, 'Board', Number(boardId), 'board_edit');
};

export const selectIsOwnerBoard = (state) => {
  const { boardId } = selectPath(state);

  if (!boardId) {
    return false;
  }

  return selectHasPermission(state, 'Board', Number(boardId), 'board_admin');
};

export const selectMembershipsForCurrentBoard = createSelector(
  orm,
  (state) => selectPath(state).boardId,
  ({ Board, User }, boardId) => {
    if (!boardId) {
      return [];
    }

    const boardModel = Board.withId(boardId);

    if (!boardModel) {
      return [];
    }

    // Tous les utilisateurs du board sont déjà chargés
    // via boardUsers dans l'API
    return User.all().toRefArray();
  },
);

export const selectRoleAssignmentsForCurrentBoard = createSelector(
  orm,
  (state) => selectPath(state).boardId,
  ({ Board, UserRoleAssignment }, boardId) => {
    if (!boardId) {
      return [];
    }

    const board = Board.withId(Number(boardId));

    if (!board) {
      return [];
    }

    return UserRoleAssignment.all()
      .toRefArray()
      .filter((assignment) => {
        return (
          (assignment.resourceType === ResourceTypes.BOARD &&
            assignment.resourceId === Number(boardId)) ||
          (assignment.resourceType === ResourceTypes.PROJECT &&
            assignment.resourceId === board.projectId)
        );
      });
  },
);

export const selectLabelsForCurrentBoard = createSelector(
  orm,
  (state) => selectPath(state).boardId,
  ({ Board }, id) => {
    if (!id) {
      return id;
    }

    const boardModel = Board.withId(id);

    if (!boardModel) {
      return boardModel;
    }

    return boardModel
      .getOrderedLabelsQuerySet()
      .toRefArray()
      .map((label) => ({
        ...label,
        isPersisted: !isLocalId(label.id),
      }));
  },
);

export const selectListIdsForCurrentBoard = createSelector(
  orm,
  (state) => selectPath(state).boardId,
  ({ Board }, id) => {
    if (!id) {
      return id;
    }

    const boardModel = Board.withId(id);

    if (!boardModel) {
      return boardModel;
    }

    return boardModel
      .getOrderedListsQuerySet()
      .toRefArray()
      .map((list) => list.id);
  },
);

export const selectListsForCurrentBoard = createSelector(
  orm,
  (state) => selectPath(state).boardId,
  ({ Board }, id) => {
    if (!id) {
      return id;
    }

    const boardModel = Board.withId(id);

    if (!boardModel) {
      return boardModel;
    }

    return boardModel
      .getOrderedListsQuerySet()
      .toRefArray()
      .map((list) => ({
        ...list,
        isPersisted: !isLocalId(list.id),
      }));
  },
);

export const selectCardsForCurrentBoard = createSelector(
  orm,
  (state) => selectPath(state).boardId,
  ({ Board }, id) => {
    if (!id) {
      return id;
    }

    const boardModel = Board.withId(id);

    if (!boardModel) {
      return boardModel;
    }

    return boardModel.lists.toModelArray().flatMap((listModel) =>
      listModel.getFilteredOrderedCardsModelArray().map((cardModel) => ({
        ...cardModel.ref,
        isPersisted: !isLocalId(cardModel.id),
      })),
    );
  },
);

// Enhanced selector that includes related data - use with caution as it may impact performance
export const selectCardsWithDetailsForCurrentBoard = createSelector(
  orm,
  (state) => selectPath(state).boardId,
  ({ Board }, id) => {
    if (!id) {
      return id;
    }

    const boardModel = Board.withId(id);

    if (!boardModel) {
      return boardModel;
    }

    // Get filtered cards from each list instead of all cards from board
    const filteredCards = [];
    const lists = boardModel.lists.toModelArray();

    lists.forEach((listModel) => {
      const listFilteredCards = listModel.getFilteredOrderedCardsModelArray();
      listFilteredCards.forEach((cardModel) => {
        filteredCards.push({
          ...cardModel.ref,
          isPersisted: !isLocalId(cardModel.id),
          coverUrl: cardModel.coverAttachment && cardModel.coverAttachment.coverUrl,
          users: cardModel.users.toRefArray(),
          labels: cardModel.labels.toRefArray(),
          attachments: cardModel
            .getOrderedAttachmentsQuerySet()
            .toRefArray()
            .map((attachment) => ({
              ...attachment,
              isCover: attachment.id === cardModel.coverAttachmentId,
              isPersisted: !isLocalId(attachment.id),
            })),
          attachmentsTotal: cardModel.attachments.count(),
          notificationsTotal: cardModel.getUnreadNotificationsQuerySet().count(),
          lastActivityId: cardModel.getFilteredOrderedInCardActivitiesQuerySet().last()?.id,
        });
      });
    });

    return filteredCards;
  },
);

export const makeSelectHierarchyLayoutByBoardId = () =>
  createSelector(
    orm,
    (_, boardId) => boardId,
    ({ Board }, boardId) => {
      const boardModel = Board.withId(boardId);
      if (!boardModel) {
        return null;
      }

      const cards = boardModel.lists
        .toModelArray()
        .flatMap((listModel) => listModel.getFilteredOrderedCardsModelArray());

      const levelById = new Map();
      const visiting = new Set(); // détection de cycle (défensif)

      // ENFANTS = incoming SUB_TASK -> card1
      const childrenOf = (cardModel) =>
        cardModel.incomingRelations
          .toModelArray()
          .filter((rel) => rel.type === RelationTypes.SUB_TASK)
          .map((rel) => rel.card1)
          .filter(Boolean);

      // ─── 5a : parent direct d'une carte (outgoing SUB_TASK → card2), cohérent §3 ───
      const parentIdOf = (cardModel) => {
        const parentRelation = cardModel.outgoingRelations
          .toModelArray()
          .find((rel) => rel.type === RelationTypes.SUB_TASK);
        return parentRelation && parentRelation.card2 ? parentRelation.card2.id : null;
      };

      // const listPosition = (listId) => {
      //   const listModel = listId ? List.withId(listId) : null;
      //   return listModel ? listModel.position : Infinity;
      // };

      const resolve = (cardModel) => {
        if (levelById.has(cardModel.id)) {
          return { level: levelById.get(cardModel.id) };
        }

        if (visiting.has(cardModel.id)) {
          return { level: 0 };
        }

        visiting.add(cardModel.id);

        const children = childrenOf(cardModel);

        let level = 0;

        if (children.length > 0) {
          level = Math.max(...children.map((child) => resolve(child).level)) + 1;
        }

        visiting.delete(cardModel.id);

        levelById.set(cardModel.id, level);

        return { level };
      };

      cards.forEach(resolve);

      const maxLevel = levelById.size > 0 ? Math.max(...levelById.values()) : 0;
      const lanes = Array.from({ length: maxLevel + 1 }, () => ({}));

      cards.forEach((cardModel) => {
        const level = levelById.get(cardModel.id);
        const { listId } = cardModel;

        if (!lanes[level][listId]) {
          lanes[level][listId] = [];
        }

        lanes[level][listId].push(cardModel.id);
      });

      // ─── 5a : groupement des sous-tâches par (parent × listId RÉEL) ───
      // groups[listId] = { parentGroups: { [parentId]: [cardIds] }, orphanCardIds: [] }
      // ⚠️ On groupe sur le listId RÉEL de la sous-tâche (sa vraie colonne),
      //    PAS sur derivedListId (qui ne sert qu'à placer le parent).
      // ⚠️ Conservé tel quel pour le Kanban (unification prévue en C2-final).
      const groups = {};

      cards.forEach((cardModel) => {
        const { listId } = cardModel; // ← listId RÉEL
        if (!listId) return;

        const parentId = parentIdOf(cardModel);

        if (!groups[listId]) {
          groups[listId] = { parentGroups: {}, orphanCardIds: [] };
        }

        if (parentId === null) {
          groups[listId].orphanCardIds.push(cardModel.id);
        } else {
          if (!groups[listId].parentGroups[parentId]) {
            groups[listId].parentGroups[parentId] = [];
          }
          groups[listId].parentGroups[parentId].push(cardModel.id);
        }
      });

      // ─── 5a : fallback "une seule sous-tâche de ce parent dans cette liste" ───
      //          → pas d'encadré, la carte repart en rendu plat (orphanCardIds)
      Object.values(groups).forEach((cell) => {
        const currentCell = cell;
        Object.keys(currentCell.parentGroups).forEach((parentId) => {
          const childIds = currentCell.parentGroups[parentId];
          if (childIds.length < 2) {
            currentCell.orphanCardIds.push(...childIds);
            delete currentCell.parentGroups[parentId];
          }
        });
      });

      // ─── C2 : groupement par (NIVEAU × colonne de placement) pour les COULOIRS ───
      // groupsByLevel[level][placementListId] = { parentGroups: { [parentId]: [ids] }, orphanCardIds: [] }
      //
      // Colonne de placement = (le MÊME que lanes) :
      //   - feuilles → leur listId réel (car derivedListId === listId)
      //   - parents  → on fait le calcul danas la back end donc on prend aussi le listId
      // Regroupement uniquement si MÊME parent direct ET MÊME cellule (niveau × colonne),
      // exactement la règle du Kanban étendue à chaque niveau.
      const groupsByLevel = {};

      cards.forEach((cardModel) => {
        const level = levelById.get(cardModel.id);
        const placementListId = cardModel.listId;
        if (placementListId === undefined || placementListId === null) return;

        const parentId = parentIdOf(cardModel);

        if (!groupsByLevel[level]) {
          groupsByLevel[level] = {};
        }
        if (!groupsByLevel[level][placementListId]) {
          groupsByLevel[level][placementListId] = { parentGroups: {}, orphanCardIds: [] };
        }

        const cell = groupsByLevel[level][placementListId];

        if (parentId === null) {
          cell.orphanCardIds.push(cardModel.id);
        } else {
          if (!cell.parentGroups[parentId]) {
            cell.parentGroups[parentId] = [];
          }
          cell.parentGroups[parentId].push(cardModel.id);
        }
      });

      // ─── C2 : fallback "< 2 sous-tâches du même parent dans cette cellule" → orphelin ───
      Object.values(groupsByLevel).forEach((levelCells) => {
        Object.values(levelCells).forEach((cell) => {
          const currentCell = cell;
          Object.keys(currentCell.parentGroups).forEach((parentId) => {
            const childIds = currentCell.parentGroups[parentId];
            if (childIds.length < 2) {
              currentCell.orphanCardIds.push(...childIds);
              delete currentCell.parentGroups[parentId];
            }
          });
        });
      });

      return {
        lanes,
        maxLevel,
        levelById: Object.fromEntries(levelById),
        groups, // ─── 5a : Kanban (inchangé) ───
        groupsByLevel, // ─── C2 : Couloirs ───
      };
    },
  );

export const selectHierarchyLayoutByBoardId = makeSelectHierarchyLayoutByBoardId();
export const selectHierarchyLayoutForCurrentBoard = (state) =>
  selectHierarchyLayoutByBoardId(state, selectPath(state).boardId);

export const selectFilterUsersForCurrentBoard = createSelector(
  orm,
  (state) => selectPath(state).boardId,
  ({ Board }, id) => {
    if (!id) {
      return id;
    }

    const boardModel = Board.withId(id);

    if (!boardModel) {
      return boardModel;
    }

    return boardModel.filterUsers.toRefArray();
  },
);

export const selectIncludeCardsWithoutMembersForCurrentBoard = createSelector(
  orm,
  (state) => selectPath(state).boardId,
  ({ Board }, id) => {
    if (!id) {
      return false;
    }

    const boardModel = Board.withId(id);

    if (!boardModel) {
      return false;
    }

    return boardModel.includeCardsWithoutMembers || false;
  },
);

export const selectIncludeCardsWithoutLabelsForCurrentBoard = createSelector(
  orm,
  (state) => selectPath(state).boardId,
  ({ Board }, id) => {
    if (!id) {
      return false;
    }

    const boardModel = Board.withId(id);

    if (!boardModel) {
      return false;
    }

    return boardModel.includeCardsWithoutLabels || false;
  },
);

export const selectFilterLabelsForCurrentBoard = createSelector(
  orm,
  (state) => selectPath(state).boardId,
  ({ Board }, id) => {
    if (!id) {
      return id;
    }

    const boardModel = Board.withId(id);

    if (!boardModel) {
      return boardModel;
    }

    return boardModel.filterLabels.toRefArray();
  },
);

export const selectFilterTextForCurrentBoard = createSelector(
  orm,
  (state) => selectPath(state).boardId,
  ({ Board }, id) => {
    if (!id) {
      return id;
    }

    const boardModel = Board.withId(id);

    if (!boardModel) {
      return boardModel;
    }

    return boardModel.filterText;
  },
);

export const selectIsBoardWithIdExists = createSelector(
  orm,
  (_, id) => id,
  ({ Board }, id) => Board.idExists(id),
);

export const makeSelectCurrentUserRoleAssignmentForBoardById = () =>
  createSelector(
    orm,
    (_, boardId) => boardId,
    (state) => selectCurrentUserId(state),
    ({ UserRoleAssignment }, boardId, currentUserId) => {
      if (!boardId) {
        return null;
      }

      return (
        UserRoleAssignment.filter({
          userId: currentUserId,
          resourceType: 'Board',
          resourceId: Number(boardId),
        }).first()?.ref || null
      );
    },
  );

export default {
  makeSelectBoardById,
  selectBoardById,
  selectCurrentBoard,
  selectRoleAssignmentsForCurrentBoard,
  selectMembershipsForCurrentBoard,
  selectCanViewBoard,
  selectLabelsForCurrentBoard,
  selectListIdsForCurrentBoard,
  selectListsForCurrentBoard,
  selectCardsForCurrentBoard,
  selectCardsWithDetailsForCurrentBoard,
  selectFilterUsersForCurrentBoard,
  selectIncludeCardsWithoutMembersForCurrentBoard,
  selectIncludeCardsWithoutLabelsForCurrentBoard,
  selectFilterLabelsForCurrentBoard,
  selectFilterTextForCurrentBoard,
  selectIsBoardWithIdExists,
  makeSelectHierarchyLayoutByBoardId,
  selectHierarchyLayoutByBoardId,
  selectHierarchyLayoutForCurrentBoard,
  selectCanEditBoard,
  selectIsOwnerBoard,
  makeSelectCurrentUserRoleAssignmentForBoardById,
};
